import classnames from 'classnames'
import * as React from 'react'
import { Dialog, useDialogState } from 'reakit/Dialog'
import { Portal } from 'reakit/Portal'

import { getVisualViewport, useVisualViewport } from '../../hooks/useVisualViewport'
import { COMPONENT_ANIMATION_DURATION } from '../constants'
import { getExtendedDialogState } from '../dialog'
import { NotificationContext, NotificationOptions, NotificationPosition } from './hooks'

interface NotificationState {
  element: JSX.Element
  options: Partial<NotificationOptions>
}

export function NotificationWrapper(props: React.PropsWithChildren<any>) {
  const state = useDialogState({ modal: false, animated: COMPONENT_ANIMATION_DURATION })
  const [notification, setNotification] = React.useState<NotificationState | null>(null)
  const { isFullyClosed, isFullyOpened } = getExtendedDialogState(state)
  const { viewport, setViewport } = useVisualViewport({ enabled: !!notification })

  const context = React.useRef({
    show: (element: JSX.Element, options: Partial<NotificationOptions> = {}) => {
      setViewport(() => getVisualViewport(window.visualViewport))
      setNotification({ element, options })
      state.show()
    },
    hide: state.hide,
  })

  const positionClass = React.useMemo(() => {
    const { position = NotificationPosition.BottomLeft } = notification?.options ?? {}
    return {
      '[--inset-t:1rem] items-start pt-inset-top': [
        NotificationPosition.TopLeft,
        NotificationPosition.TopRight,
      ].includes(position),
      '[--inset-r:1rem] justify-end landscape-secondary:pr-inset-right': [
        NotificationPosition.TopRight,
        NotificationPosition.BottomRight,
      ].includes(position),
      '[--inset-b:1rem] items-end pb-inset-bottom': [
        NotificationPosition.BottomLeft,
        NotificationPosition.BottomRight,
      ].includes(position),
      '[--inset-l:1rem] justify-start landscape-primary:pl-inset-left': [
        NotificationPosition.TopLeft,
        NotificationPosition.BottomLeft,
      ].includes(position),
    }
  }, [notification?.options])

  const WrapperComponent = React.useMemo(
    () => (notification?.options.inline ? React.Fragment : Portal),
    [notification?.options.inline]
  )

  return (
    <NotificationContext.Provider value={context.current}>
      {props.children}
      {!isFullyClosed && (
        <WrapperComponent>
          <Dialog
            {...state}
            aria-label="Notification"
            hideOnEsc={false}
            hideOnClickOutside={false}
            unstable_autoFocusOnShow={false}
          >
            {({ onTransitionEnd, onAnimationEnd, ...innerProps }) => (
              <div
                {...innerProps}
                role="alert"
                className="finviz-modal pointer-events-none fixed left-0 top-0 z-notification outline-none"
                style={{
                  transform: `translate(${viewport.left}px, ${viewport.top}px)`,
                }}
              >
                <div
                  onTransitionEnd={onTransitionEnd}
                  onAnimationEnd={onAnimationEnd}
                  className={classnames(positionClass, 'flex max-w-full origin-top-left p-2 sm:p-5', {
                    '!transition-none': isFullyOpened,
                  })}
                  style={{
                    width: viewport.width,
                    height: viewport.height,
                    transform: `scale(${viewport.scale})`,
                    willChange: 'auto',
                  }}
                >
                  {notification?.element}
                </div>
              </div>
            )}
          </Dialog>
        </WrapperComponent>
      )}
    </NotificationContext.Provider>
  )
}
